import { useLiveQuery } from 'dexie-react-hooks'
import { motion } from 'framer-motion'
import { Search, Users, Wallet } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { ClientDetail, TIP_META, TipBadge } from '@/components/ClientDetail'
import { ScreenHeader } from '@/components/ScreenHeader'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useApp } from '@/context'
import type { ClientTip } from '@/db/dexie'
import { db } from '@/db/dexie'
import { getClienti } from '@/db/queries'
import { fmtDataRelativa, fmtRON, fmtSuma } from '@/lib/format'
import { cn } from '@/lib/utils'

const TIPURI = Object.keys(TIP_META) as ClientTip[]

export default function Clienti() {
  const { selectedId, setSelectedId, activeTab } = useApp()
  const clienti = useLiveQuery(() => getClienti(), [])
  const comenzi = useLiveQuery(() => db.comenzi.toArray(), []) ?? []

  const [cauta, setCauta] = useState('')
  const [filtruTip, setFiltruTip] = useState<'toti' | ClientTip>('toti')
  const [openId, setOpenId] = useState<string | null>(null)

  // deep-link: another screen asked to focus a specific client
  useEffect(() => {
    if (activeTab === 'clienti' && selectedId) {
      setOpenId(selectedId)
      setSelectedId(null)
    }
  }, [activeTab, selectedId, setSelectedId])

  const comenziPerClient = useMemo(() => {
    const m = new Map<string, number>()
    for (const c of comenzi) m.set(c.clientId, (m.get(c.clientId) ?? 0) + 1)
    return m
  }, [comenzi])

  const filtrate = useMemo(() => {
    if (!clienti) return []
    const q = cauta.trim().toLowerCase()
    return clienti.filter((c) => {
      if (filtruTip !== 'toti' && c.tip !== filtruTip) return false
      if (!q) return true
      return c.nume.toLowerCase().includes(q) || c.localitate.toLowerCase().includes(q)
    })
  }, [clienti, cauta, filtruTip])

  const restantiTotal = useMemo(
    () => (clienti ?? []).reduce((s, c) => s + c.soldRestant, 0),
    [clienti],
  )
  const nrRestanti = useMemo(
    () => (clienti ?? []).filter((c) => c.soldRestant > 0).length,
    [clienti],
  )

  return (
    <div>
      <ScreenHeader tab="clienti">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={cauta}
            onChange={(e) => setCauta(e.target.value)}
            placeholder="Caută nume sau localitate…"
            className="h-9 w-56 pl-8"
          />
        </div>
        <Select value={filtruTip} onValueChange={(v) => setFiltruTip(v as 'toti' | ClientTip)}>
          <SelectTrigger className="h-9 w-40">
            <SelectValue placeholder="Tip client" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="toti">Toate tipurile</SelectItem>
            {TIPURI.map((t) => (
              <SelectItem key={t} value={t}>
                {TIP_META[t].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ScreenHeader>

      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3.5 py-2 text-sm shadow-paper">
          <Users className="size-4 text-amber-ink" />
          <span className="text-muted-foreground">Clienți activi</span>
          <span className="font-mono font-semibold nums text-foreground">
            {clienti?.length ?? 0}
          </span>
        </div>
        <div
          className={cn(
            'inline-flex items-center gap-2 rounded-lg border px-3.5 py-2 text-sm shadow-paper',
            nrRestanti > 0 ? 'border-tomato/25 bg-tomato-soft' : 'border-border bg-card',
          )}
        >
          <Wallet className={cn('size-4', nrRestanti > 0 ? 'text-tomato' : 'text-amber-ink')} />
          <span className={cn(nrRestanti > 0 ? 'text-tomato' : 'text-muted-foreground')}>
            Sold restant
          </span>
          <span
            className={cn(
              'font-mono font-semibold nums',
              nrRestanti > 0 ? 'text-tomato' : 'text-foreground',
            )}
          >
            {fmtRON(restantiTotal)}
          </span>
          {nrRestanti > 0 && <span className="text-xs text-tomato/80">· {nrRestanti} clienți</span>}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card shadow-paper">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Client</TableHead>
              <TableHead className="hidden sm:table-cell">Tip</TableHead>
              <TableHead className="hidden md:table-cell">Localitate</TableHead>
              <TableHead className="hidden lg:table-cell">Telefon</TableHead>
              <TableHead className="text-right">Comenzi</TableHead>
              <TableHead className="text-right">Sold restant</TableHead>
              <TableHead className="hidden sm:table-cell text-right">Ultima comandă</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtrate.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground">
                  Niciun client nu se potrivește filtrelor.
                </TableCell>
              </TableRow>
            )}
            {filtrate.map((c, i) => (
              <motion.tr
                key={c.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: Math.min(i * 0.03, 0.3) }}
                onClick={() => setOpenId(c.id)}
                className="cursor-pointer border-b border-border transition-colors hover:bg-accent/50"
              >
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-amber-soft font-display text-sm font-medium text-amber-ink">
                      {c.nume.charAt(0)}
                    </span>
                    <div className="min-w-0">
                      <div className="font-medium">{c.nume}</div>
                      <div className="text-xs text-muted-foreground sm:hidden">{c.localitate}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  <TipBadge tip={c.tip} />
                </TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground">
                  {c.localitate}
                </TableCell>
                <TableCell className="hidden lg:table-cell font-mono text-sm nums text-muted-foreground">
                  {c.telefon}
                </TableCell>
                <TableCell className="text-right font-mono text-sm nums">
                  {comenziPerClient.get(c.id) ?? 0}
                </TableCell>
                <TableCell className="text-right">
                  {c.soldRestant > 0 ? (
                    <span className="font-mono text-sm font-semibold nums text-tomato">
                      {fmtSuma(c.soldRestant)}
                    </span>
                  ) : (
                    <span className="font-mono text-sm nums text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="hidden sm:table-cell text-right text-sm text-muted-foreground">
                  {fmtDataRelativa(c.dataUltimaComanda)}
                </TableCell>
              </motion.tr>
            ))}
          </TableBody>
        </Table>
      </div>

      <ClientDetail clientId={openId} onClose={() => setOpenId(null)} />
    </div>
  )
}
